/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Fragment, useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import SectionLink from '@atlaskit/gatsby-theme-brisk/src/components/section-link';
import Snippet from '@atlaskit/gatsby-theme-brisk/src/components/snippet';
import EmptyState from '@atlaskit/empty-state';
import ErrorBoundary from '@atlaskit/gatsby-theme-brisk/src/components/error-boundary';
import DoDont from '@atlaskit/gatsby-theme-brisk/src/components/do-dont';
import { ContentRenderingError } from './content-error';
import resolveLocalisation from '../utils/resolveLocalisation';
import Table, {
  TableCell,
  TableRow,
  Table2ColumnRow,
} from './content-types/table';
import AssetCard from './content-types/asset-card';
import ColorCard from './content-types/color-card';

const slugify = require('github-slugger').slug;

export const buildURL = (slugs: string[] = []) => {
  // ['path', 'subpath'] => /path/subpath
  let URL = slugs
    .map((s) => {
      if (s) {
        return `/${resolveLocalisation(s)}`;
      }
      return '';
    })
    .join('')
    .toLocaleLowerCase();
  return URL;
};

const OEmbed = ({ src }) => {
  // TODO DON'T DO THIS, FIND A SAFER WAY
  // USE A ROBUST LIBRARY, WE'rE HARD CODDNG THE VIMEO REQUEST RIGHT NOW
  const [embed, setEmbed] = useState(null);
  useEffect(() => {
    fetch(`https://vimeo.com/api/oembed.json?url=${src}`)
      .then((res) => res.json())
      .then((data) => {
        setEmbed(data.html);
      })
      .catch((err) => {});
  }, [src]);
  return <div dangerouslySetInnerHTML={{ __html: embed }} />;
};

const getContentType = (contentType) => {
  if (contentType && contentType.sys) {
    return contentType.sys.id;
  }
  return null;
};

export const getEntryHyperlink = (slug, category, parent) => {
  const parentSlug = resolveLocalisation(parent)?.fields.slug;
  return buildURL([category, parentSlug, slug]);
};

const options = (images) => ({
  renderMark: {
    [MARKS.CODE]: (text) => <Snippet>{text}</Snippet>,
  },
  renderNode: {
    ['entry-hyperlink']: (node, children) => {
      const { slug, category, parent } = node.data.target.fields;
      const link = getEntryHyperlink(slug, category, parent);
      return <Link to={link}>{children}</Link>;
    },
    ['embedded-entry-inline']: (node, children) => {
      const contentType = getContentType(node.data.target.sys.contentType);
      switch (contentType) {
        case 'videEmbed': {
          if (node.data.target.fields.url) {
            return <OEmbed src={node.data.target.fields.url} />;
          }
          return children;
        }
        default:
          return children;
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      const contentType = getContentType(node.data.target.sys.contentType);
      switch (contentType) {
        case 'table2Columns':
          return (
            <Table
              node={node}
              options={options(images)}
              renderHeading={(Component, { fields: tableFields }) => {
                const heading1 = resolveLocalisation(tableFields.heading1);
                const heading2 = resolveLocalisation(tableFields.heading2);
                return <Component headings={[heading1, heading2]} />;
              }}
            />
          );
        case 'columnTableRow':
          return (
            <Table2ColumnRow
              key={node.data.target.sys.id}
              node={node}
              options={options(images)}
            />
          );
        case 'table': {
          return <Table node={node} options={options(images)} />;
        }
        case 'tableRow': {
          return (
            <TableRow
              key={node.data.target.sys.id}
              node={node}
              options={options(images)}
            />
          );
        }
        case 'tableCell': {
          return (
            <TableCell
              key={node.data.target.sys.id}
              node={node}
              options={options(images)}
            />
          );
        }
        case 'assetCard': {
          const { asset, title, thumbnail } = resolveLocalisation(
            node.data.target.fields,
          );
          const assetFields = resolveLocalisation(asset);
          const assetFileFields = resolveLocalisation(assetFields.fields.file);
          const thumbnailFields = resolveLocalisation(thumbnail);

          return (
            <AssetCard
              title={resolveLocalisation(title)}
              link={assetFileFields.url}
              thumbnail={
                thumbnailFields &&
                resolveLocalisation(thumbnailFields.fields.file).url
              }
              fileSize={assetFileFields.details.size}
            />
          );
        }
        case 'assetsContainer': {
          const fields = node.data.target.fields;
          const children = resolveLocalisation(fields.children);
          return documentToReactComponents(children, options(images));
        }
        case 'dosAndDonts': {
          const fields = node.data.target.fields;
          const fullWidth = !!resolveLocalisation(fields.fullWidth);
          const type =
            resolveLocalisation(fields.type) === 'Do' ? 'do' : 'dont';
          const image = resolveLocalisation(fields.image);
          const text = resolveLocalisation(fields.text);
          let description, file;
          if (image) {
            description = resolveLocalisation(image.fields.description);
            file = resolveLocalisation(image.fields.file);
          }
          return (
            <DoDont
              fullWidth={fullWidth}
              type={type}
              image={image && { alt: description, url: file.url }}
            >
              {text}
            </DoDont>
          );
        }
        case 'colorCard': {
          const fields = node.data.target.fields;
          const color = resolveLocalisation(fields.hexCode);
          const name = resolveLocalisation(fields.name);
          return <ColorCard hexColor={color} name={name} />;
        }
        default: {
          return children;
        }
      }
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const file = resolveLocalisation(node.data.target.fields.file);
      const image = images.find((img) => file && file.url === img.url);
      if (!file) {
        return <EmptyState header="Something went wrong" />;
      }
      return (
        <img
          css={{ maxWidth: '100%' }}
          alt={file.description || ''}
          src={file.url}
        />
      );
    },
    [BLOCKS.PARAGRAPH]: (node, children) => {
      // remove empty paragraphs
      if (!children.every((item) => item === '')) {
        return <p style={{ whiteSpace: 'pre-wrap' }}>{children}</p>;
      }
      return children;
    },
    [BLOCKS.HEADING_2]: (node, children) => {
      return (
        <SectionLink level={2} id={slugify(node.content[0].value)}>
          {children}
        </SectionLink>
      );
    },
    [BLOCKS.HEADING_3]: (node, children) => {
      return (
        <SectionLink level={3} id={slugify(node.content[0].value)}>
          {children}
        </SectionLink>
      );
    },
  },
});

const ContentfulRenderer = ({ json, images }) => {
  return (
    <Fragment>{documentToReactComponents(json, options(images))}</Fragment>
  );
};

const ContentRenderer = ({
  children,
  images = [],
  json,
}: ContentRendererProps) => {
  return (
    <ErrorBoundary fallback={<ContentRenderingError />}>
      <ContentfulRenderer images={images} json={json} />
      {children}
    </ErrorBoundary>
  );
};

interface ContentRendererProps {
  json: any;
  images?: any[];
  children?: React.ReactNode;
}

export default ContentRenderer;
